/**
 * Synthetic Non Veg Lunch (= Combo 1 + Combo 2) aggregate helpers.
 * Used in Restaurant Forecasts / Item Deep Dive / All Runs item charts.
 */

/** Canonical id + display name for Combo 1 + Combo 2 sum */
export const COMBO_AGGREGATE_ITEMNAME = 'Non Veg Lunch';
/** Legacy ids still accepted for matching */
export const COMBO_AGGREGATE_ALIASES = [
  'non veg lunch',
  'non veg combo 1 + combo 2',
  'nonvegcombo1+combo2',
  'nonvegcombo1combo2',
];

export const COMBO_SOURCE_ITEMS = ['non veg combo 1', 'non veg combo 2'];

/** Display order: Combo 1, Combo 2, Non Veg Lunch (sum), Veg Lunch */
export const ITEM_DISPLAY_ORDER = [
  'non veg combo 1',
  'non veg combo 2',
  'non veg lunch',
  'veg lunch',
];

export function normalizeItemKey(itemname) {
  return String(itemname || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function isComboAggregateItem(itemname) {
  const n = normalizeItemKey(itemname);
  const compact = n.replace(/\s+/g, '');
  return (
    n === normalizeItemKey(COMBO_AGGREGATE_ITEMNAME) ||
    COMBO_AGGREGATE_ALIASES.includes(n) ||
    COMBO_AGGREGATE_ALIASES.includes(compact) ||
    compact === 'nonveglunch'
  );
}

export function isComboSourceItem(itemname) {
  const n = normalizeItemKey(itemname);
  return COMBO_SOURCE_ITEMS.includes(n);
}

/** Pretty label for tables / dropdowns */
export function displayItemLabel(itemname) {
  if (isComboAggregateItem(itemname)) return COMBO_AGGREGATE_ITEMNAME;
  const n = normalizeItemKey(itemname);
  if (n === 'non veg combo 1') return 'Non Veg Combo 1';
  if (n === 'non veg combo 2') return 'Non Veg Combo 2';
  if (n === 'veg lunch') return 'Veg Lunch';
  return String(itemname || '')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function itemDisplaySortIndex(itemname) {
  const n = normalizeItemKey(itemname);
  if (isComboAggregateItem(itemname)) {
    return ITEM_DISPLAY_ORDER.indexOf('non veg lunch');
  }
  const idx = ITEM_DISPLAY_ORDER.indexOf(n);
  return idx >= 0 ? idx : 1000;
}

export function compareItemDisplayOrder(a, b) {
  const ia = itemDisplaySortIndex(a);
  const ib = itemDisplaySortIndex(b);
  if (ia !== ib) return ia - ib;
  return displayItemLabel(a).localeCompare(displayItemLabel(b));
}

/** Map mg_data item_type → chart item(s) */
export function mgTypeForItem(itemname) {
  const n = normalizeItemKey(itemname);
  if (n === 'veg lunch' || n.replace(/\s+/g, '') === 'veglunch') return 'veg_lunch';
  if (isComboAggregateItem(itemname)) return 'non_veg_lunch';
  return null;
}

export function sumFinite(...vals) {
  let total = 0;
  let any = false;
  vals.forEach((v) => {
    const n = parseFloat(v);
    if (Number.isFinite(n)) {
      total += n;
      any = true;
    }
  });
  return any ? total : null;
}

/** Parse "HH:MM" / "HH:MM:SS" to minutes from midnight */
export function timeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = String(timeStr).trim().split(/[:\s]+/);
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

export function minutesToTime(totalMin) {
  if (!Number.isFinite(totalMin) || totalMin < 0) return '';
  const h = Math.floor(totalMin / 60);
  const m = Math.round(totalMin % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Across Combo 1 + Combo 2 on the same day:
 * first_order_time = earliest start, last_order_time = latest end.
 */
export function mergeSalePeriodTimes(dayRows = []) {
  const starts = [];
  const ends = [];
  const coverages = [];
  dayRows.forEach((r) => {
    const s = timeToMinutes(r?.first_order_time);
    const e = timeToMinutes(r?.last_order_time);
    if (s != null) starts.push(s);
    if (e != null) ends.push(e);
    const c = parseFloat(r?.revenue_coverage);
    if (Number.isFinite(c)) coverages.push(c);
  });
  if (!starts.length || !ends.length) {
    return { first_order_time: '', last_order_time: '', revenue_coverage: null };
  }
  const startMin = Math.min(...starts);
  const endMin = Math.max(...ends);
  return {
    first_order_time: minutesToTime(startMin),
    last_order_time: minutesToTime(endMin),
    revenue_coverage: coverages.length
      ? coverages.reduce((a, b) => a + b, 0) / coverages.length
      : null,
  };
}

function dayApe(actual, forecast) {
  if (!(actual > 0) || !Number.isFinite(forecast)) return null;
  return Math.min(Math.abs(actual - forecast) / actual, 1);
}

function dayMad(actual, forecast) {
  if (!(actual > 0) || !Number.isFinite(forecast)) return null;
  return Math.abs(actual - forecast);
}

function statusFromMapeMad(mape, mad) {
  if (mape == null || !Number.isFinite(mape)) return null;
  const accuracyPct = (1 - mape) * 100;
  if (accuracyPct > 90) return 'Green';
  if (accuracyPct >= 70) return 'Orange';
  return 'Red';
}

/**
 * Weighted MAPE/MAD/status from day-level actual vs forecast (sum grain).
 */
export function summarizeComboAggregateMetrics(dayRows = []) {
  let weightedMape = 0;
  let weightedMad = 0;
  let weight = 0;
  let daysSold = 0;
  let totalActual = 0;
  let inRangeHits = 0;
  let inRangeTotal = 0;

  dayRows.forEach((row) => {
    const actual = parseFloat(row.actual_qty ?? row.actual ?? 0);
    const forecast = parseFloat(
      row.forecast_qty_component_model ?? row.fridComponent ?? row.forecast_qty ?? row.forecast
    );
    if (!Number.isFinite(actual) || actual <= 0) return;
    daysSold += 1;
    totalActual += actual;
    const ape = Number.isFinite(parseFloat(row.APE_component_model))
      ? parseFloat(row.APE_component_model)
      : dayApe(actual, forecast);
    const mad = Number.isFinite(parseFloat(row.MAD_component_model))
      ? parseFloat(row.MAD_component_model)
      : dayMad(actual, forecast);
    if (Number.isFinite(ape)) {
      weightedMape += ape * actual;
      weight += actual;
    }
    if (Number.isFinite(mad)) {
      weightedMad += mad * actual;
    }
    const lo = parseFloat(row.forecast_qty_lower_component_model ?? row.forecastLower ?? row.forecast_lower);
    const hi = parseFloat(row.forecast_qty_upper_component_model ?? row.forecastUpper ?? row.forecast_upper);
    if (Number.isFinite(lo) && Number.isFinite(hi)) {
      inRangeTotal += 1;
      if (actual >= lo && actual <= hi) inRangeHits += 1;
    }
  });

  const mape = weight > 0 ? weightedMape / weight : null;
  const mad = weight > 0 ? weightedMad / weight : null;
  return {
    mape,
    mad,
    status: statusFromMapeMad(mape, mad),
    num_days: daysSold,
    avg_daily_units_sold: daysSold > 0 ? totalActual / daysSold : null,
    inRangePct: inRangeTotal > 0 ? inRangeHits / inRangeTotal : null,
    inRangeHits,
    inRangeTotal,
    avg_mape_component_model: mape,
    avg_mad_component_model: mad,
    APE_component_model: mape,
    MAD_component_model: mad,
    status_component_model: statusFromMapeMad(mape, mad),
  };
}

/**
 * Merge two daily-sales style rows on the same date (sum qty / forecast / bands).
 * Descriptions are kept as desc1 | desc2 for dual-menu hover.
 */
export function mergeDailySalesRows(a, b) {
  const date = a?.date || b?.date;
  const qtyA = parseFloat(a?.quantity ?? 0) || 0;
  const qtyB = parseFloat(b?.quantity ?? 0) || 0;
  const fcA = parseFloat(a?.forecast_component ?? a?.forecast_qty_component_model ?? a?.forecast_qty);
  const fcB = parseFloat(b?.forecast_component ?? b?.forecast_qty_component_model ?? b?.forecast_qty);
  const loA = parseFloat(a?.forecast_lower ?? a?.forecast_qty_lower_component_model ?? a?.forecast_qty_lower);
  const loB = parseFloat(b?.forecast_lower ?? b?.forecast_qty_lower_component_model ?? b?.forecast_qty_lower);
  const hiA = parseFloat(a?.forecast_upper ?? a?.forecast_qty_upper_component_model ?? a?.forecast_qty_upper);
  const hiB = parseFloat(b?.forecast_upper ?? b?.forecast_qty_upper_component_model ?? b?.forecast_qty_upper);
  const descA = (a?.description || '').toString().trim();
  const descB = (b?.description || '').toString().trim();
  const descriptions = [descA, descB].filter(Boolean);
  const forecast = sumFinite(fcA, fcB);
  const lower = sumFinite(loA, loB);
  const upper = sumFinite(hiA, hiB);
  const quantity = qtyA + qtyB;
  const ape = dayApe(quantity, forecast);
  const mad = dayMad(quantity, forecast);
  return {
    date,
    quantity,
    forecast_component: forecast != null ? forecast : 0,
    forecast_qty_component_model: forecast != null ? forecast : 0,
    forecast_qty: forecast != null ? forecast : 0,
    forecast_lower: lower,
    forecast_upper: upper,
    forecast_qty_lower_component_model: lower,
    forecast_qty_upper_component_model: upper,
    description: descriptions.join(' | '),
    description_combo1: descA,
    description_combo2: descB,
    APE_component_model: ape,
    MAD_component_model: mad,
    in_range:
      quantity > 0 && Number.isFinite(lower) && Number.isFinite(upper)
        ? quantity >= lower && quantity <= upper
          ? 'Yes'
          : 'No'
        : null,
  };
}

export function aggregateDailySalesByDate(rowsA = [], rowsB = []) {
  const map = new Map();
  const put = (row) => {
    const key = String(row.date || '').slice(0, 10);
    if (!key) return;
    if (!map.has(key)) map.set(key, { a: null, b: null });
    return key;
  };
  rowsA.forEach((row) => {
    const key = put(row);
    if (key) map.get(key).a = row;
  });
  rowsB.forEach((row) => {
    const key = put(row);
    if (key) map.get(key).b = row;
  });
  return Array.from(map.entries())
    .sort((x, y) => x[0].localeCompare(y[0]))
    .map(([, { a, b }]) => mergeDailySalesRows(a || { date: b.date }, b || { date: a.date }));
}

/**
 * Aggregate comparison/strict rows for Combo 1 + Combo 2 into synthetic item rows.
 * Day-level APE/MAD computed on summed actual vs summed forecast.
 */
export function buildComboAggregateStrictRows(rows, foodcourtid, restaurant) {
  const fc = String(foodcourtid || '').trim();
  const rest = String(restaurant || '').trim();
  const relevant = (rows || []).filter((row) => {
    if (String(row.foodcourtid || '').trim() !== fc) return false;
    if (String(row.restaurant || '').trim() !== rest) return false;
    return isComboSourceItem(row.itemname);
  });
  if (relevant.length === 0) return [];

  const byDate = new Map();
  relevant.forEach((row) => {
    const date = String(row.date || '').slice(0, 10);
    if (!date) return;
    if (!byDate.has(date)) byDate.set(date, []);
    byDate.get(date).push(row);
  });

  const out = [];
  byDate.forEach((dayRows, date) => {
    const sumField = (field) => {
      let total = 0;
      let any = false;
      dayRows.forEach((r) => {
        const n = parseFloat(r[field]);
        if (Number.isFinite(n)) {
          total += n;
          any = true;
        }
      });
      return any ? total : null;
    };
    const descs = dayRows
      .map((r) => (r.description || '').toString().trim())
      .filter(Boolean);
    const actual = sumField('actual_qty') ?? 0;
    const forecast = sumField('forecast_qty_component_model') ?? sumField('forecast_qty');
    const lower = sumField('forecast_qty_lower_component_model') ?? sumField('forecast_qty_lower');
    const upper = sumField('forecast_qty_upper_component_model') ?? sumField('forecast_qty_upper');
    const ape = dayApe(actual, forecast);
    const mad = dayMad(actual, forecast);
    const salePeriod = mergeSalePeriodTimes(dayRows);
    out.push({
      ...dayRows[0],
      itemname: COMBO_AGGREGATE_ITEMNAME,
      date,
      actual_qty: actual,
      forecast_qty_component_model: forecast,
      forecast_qty_frid: sumField('forecast_qty_frid'),
      forecast_qty_old: sumField('forecast_qty_old'),
      forecast_qty_friw: sumField('forecast_qty_friw'),
      forecast_qty: forecast,
      forecast_qty_lower_component_model: lower,
      forecast_qty_upper_component_model: upper,
      forecast_qty_lower: lower,
      forecast_qty_upper: upper,
      forecast_sigma: sumField('forecast_sigma'),
      first_order_time: salePeriod.first_order_time,
      last_order_time: salePeriod.last_order_time,
      revenue_coverage: salePeriod.revenue_coverage,
      description: descs.join(' | '),
      description_combo1: dayRows.find((r) => normalizeItemKey(r.itemname) === 'non veg combo 1')?.description || '',
      description_combo2: dayRows.find((r) => normalizeItemKey(r.itemname) === 'non veg combo 2')?.description || '',
      APE_component_model: ape,
      MAD_component_model: mad,
      status_component_model: statusFromMapeMad(ape, mad),
      in_range:
        actual > 0 && Number.isFinite(lower) && Number.isFinite(upper)
          ? actual >= lower && actual <= upper
            ? 'Yes'
            : 'No'
          : null,
      is_combo_aggregate: true,
    });
  });
  return out.sort((a, b) => String(a.date).localeCompare(String(b.date)));
}
